import { MoneyBackIcon } from "@xola/icons";
import { values } from "lodash";
import React from "react";
import { Audit, LineItemObject, LineItemStatus, LineItemType, PaymentLineItem, Purchase, PurchaseItem } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";
import { getAuditData } from "@/models/Audit";

interface RefundTimelineItemProps {
    showDate?: boolean;
    change: any;
    purchase: Purchase;
}

export const RefundTimelineItem = ({ purchase, showDate = false, change }: RefundTimelineItemProps) => {
    const timeLineItemData = {
        amount: change.amount,
        name: change.name,
    };

    const hasCompleteStatus = change?.status === LineItemStatus.COMPLETE;
    const isCancelledByTravelerRefund = isCancelByTravelerAudit(purchase, change);

    if ((!hasCompleteStatus || change?.includeInAmount === false) && !isCancelledByTravelerRefund) {
        return null;
    }

    /**
     * Case when purchase was cancelled via self service and there was a refund to the credit card.
     * There will be an audit created by traveler, which contains line item to get data from.
     */
    if (isCancelledByTravelerRefund) {
        const lineItem = getCancelLineItem(change);
        timeLineItemData.amount = lineItem?.new.amount;
        timeLineItemData.name = lineItem?.new.name;
    }

    return (
        <TimelineItem
            title="Refund"
            audit={change}
            icon={<MoneyBackIcon />}
            color="danger"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="space-y-2">
                <Currency shouldRemoveTrailingZeroes={false}>{timeLineItemData.amount}</Currency>
                <TimelineItemReason>{timeLineItemData.name}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

RefundTimelineItem.supportsAudit = (
    purchase: Purchase,
    _item: PurchaseItem,
    _audit: Audit,
    _lineItem?: PaymentLineItem,
    _timezone?: string,
    change?: any,
) => {
    const isRefundItem: boolean = change?.object === LineItemObject.REFUND;

    return isRefundItem || isCancelByTravelerAudit(purchase, change);
};

const isCancelByTravelerAudit = (purchase: Purchase, change: any) => {
    const data = getAuditData(change);
    const createdBy = data?.createdBy?.id;
    const isStatusChangeAudit = data?.data?.items?.[0]?.status?.new === "canceled";
    const isCreatedByTraveler = createdBy === purchase.organizer?.id;

    return isStatusChangeAudit && isCreatedByTraveler && getCancelLineItem(change);
};

const getCancelLineItem = (audit: any) => {
    const data = getAuditData(audit);
    const lineItems = values(data?.data?.items?.[0]?.lineItems);
    const types = new Set([LineItemType.TYPE_ADHOC_ADJUSTMENT, LineItemType.REFUND]);
    const lineItem = lineItems?.find(
        (value) => value?.old === null && types.has(value?.new?.type) && value?.new?.amount,
    );

    return lineItem;
};
